import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import placeholder from '../assets/placeholder';
import { normalizeMediaList } from '../utils/media';
import { authFetch } from '../utils/authFetch';

export default function StrainDetailPage() {
  const { slug } = useParams();
  const { t } = useLang();
  const [strain, setStrain] = useState(null);
  const [cuttings, setCuttings] = useState([]);
  const token = localStorage.getItem('token');
  const loggedIn = Boolean(token);
  const decodeRole = (tok) => {
    if (!tok) return null;
    try {
      return JSON.parse(atob(tok.split('.')[1])).role;
    } catch {
      return null;
    }
  };
  const role = decodeRole(token);
  const isAdmin = role === 'superuser' || role === 'admin';

  const fetchStrain = () => {
    const loadPublic = () =>
      authFetch(`/api/strains/${slug}`, {}, { token })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => setStrain(d));

    if (!isAdmin) {
      loadPublic().catch(() => setStrain(null));
      return;
    }

    authFetch(`/api/admin/strains/slug/${slug}`, {}, { token })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          setStrain(d);
          return;
        }
        return loadPublic();
      })
      .catch(() => setStrain(null));
  };

  useEffect(() => {
    fetchStrain();

    if (isAdmin && loggedIn) {
      authFetch(`/api/strains/${slug}/cuttings`, {}, { token })
        .then((r) => (r.ok ? r.json() : []))
        .then((d) => setCuttings(Array.isArray(d) ? d : []))
        .catch(() => {});
    } else {
      setCuttings([]);
    }
  }, [slug, token, isAdmin]);

  if (!strain) {
    return (
      <div className="container mt-3">
        <p>{t('loading')}</p>
      </div>
    );
  }

  const galleryMedia = Array.isArray(strain.images)
    ? normalizeMediaList(strain.images)
    : [];
  const mediaList = galleryMedia.length
    ? galleryMedia
        .slice()
        .sort((a, b) => Number(b.isMain) - Number(a.isMain))
    : [];

  const hasCore = Boolean(
    strain.geneticsType ||
    strain.geneticsDominance ||
    strain.thcPercent ||
    strain.cbdPercent ||
    strain.floweringWeeks ||
    strain.floweringCategory ||
    strain.yield ||
    strain.plantHeight ||
    (Array.isArray(strain.terpenes) && strain.terpenes.length) ||
    (Array.isArray(strain.aromaFlavor) && strain.aromaFlavor.length) ||
    strain.effectDirection ||
    (Array.isArray(strain.effectSensations) && strain.effectSensations.length)
  );

  const hasCorePlus = Boolean(
    strain.status ||
    strain.uniqueTrait ||
    strain.positioning ||
    strain.recommendedTime ||
    strain.targetAudience
  );

  const renderTags = (items) => (
    <div className="d-flex flex-wrap gap-2">
      {items.map((item) => (
        <span key={item} className="badge bg-light text-dark border">
          {item}
        </span>
      ))}
    </div>
  );

  const mapValue = (value, map) => (value && map[value]) || value;
  const geneticsTypeLabel = mapValue(strain.geneticsType, {
    Indica: 'Indica',
    Sativa: 'Sativa',
    Hybrid: 'Hybrid',
  });
  const floweringCategoryLabel = mapValue(strain.floweringCategory, {
    Early: t('floweringEarly'),
    Medium: t('floweringMedium'),
    Late: t('floweringLate'),
  });
  const yieldLabel = mapValue(strain.yield, {
    Low: t('yieldLow'),
    Medium: t('yieldMedium'),
    High: t('yieldHigh'),
    'Very High': t('yieldVeryHigh'),
  });
  const heightLabel = mapValue(strain.plantHeight, {
    Low: t('heightLow'),
    Medium: t('heightMedium'),
    High: t('heightHigh'),
  });
  const effectDirectionLabel = mapValue(strain.effectDirection, {
    Up: t('effectUp'),
    Neutral: t('effectNeutral'),
    Down: t('effectDown'),
  });
  const statusLabel = mapValue(strain.status, {
    'R&D': t('statusRd'),
    Test: t('statusTest'),
    'Pre-commercial': t('statusPreCommercial'),
    Commercial: t('statusCommercial'),
  });
  const positioningLabel = mapValue(strain.positioning, {
    Commercial: t('positioningCommercial'),
    Boutique: t('positioningBoutique'),
    Exotic: t('positioningExotic'),
    Niche: t('positioningNiche'),
  });
  const recommendedTimeLabel = mapValue(strain.recommendedTime, {
    Day: t('recommendedDay'),
    Evening: t('recommendedEvening'),
    Night: t('recommendedNight'),
  });
  const targetAudienceLabel = mapValue(strain.targetAudience, {
    Beginners: t('audienceBeginners'),
    Experienced: t('audienceExperienced'),
    Universal: t('audienceUniversal'),
  });

  return (
    <div className="container mt-3">
      <h1 className="mb-1">{strain.name}</h1>
      {strain.slug && <div className="text-muted mb-3">{strain.slug}</div>}
      {strain.shortDescription && <p>{strain.shortDescription}</p>}
      {isAdmin && loggedIn && strain?._id && (
        <div className="mb-3">
          <Link
            className="btn btn-outline-primary btn-sm"
            to={`/reviews-admin?strainId=${encodeURIComponent(strain._id)}`}
          >
            {t('viewReviewsForStrain')}
          </Link>
        </div>
      )}
      {mediaList.length > 0 && (
        <div className="mb-3">
          {mediaList.map((media) => (
            <div key={media.publicId || media.url} className="d-inline-block me-2 mb-2">
              {media.type === 'video' ? (
                <video
                  src={media.url}
                  poster={media.thumbnailUrl || media.url}
                  controls
                  className="img-fluid"
                  style={{ maxWidth: 220 }}
                />
              ) : (
                <img
                  src={media.url}
                  alt=""
                  loading="lazy"
                  className="img-fluid"
                  style={{ maxWidth: 220 }}
                />
              )}
            </div>
          ))}
        </div>
      )}
      {hasCore && (
        <div className="mb-4">
          <h3 className="h5">{t('passportCore')}</h3>
          {geneticsTypeLabel && (
            <p className="mb-1">
              <strong>{t('geneticsType')}:</strong> {geneticsTypeLabel}
            </p>
          )}
          {strain.geneticsDominance && (
            <p className="mb-1">
              <strong>{t('geneticsDominance')}:</strong> {strain.geneticsDominance}
            </p>
          )}
          {strain.thcPercent && (
            <p className="mb-1">
              <strong>{t('thcPercent')}:</strong> {strain.thcPercent}
            </p>
          )}
          {strain.cbdPercent && (
            <p className="mb-1">
              <strong>{t('cbdPercent')}:</strong> {strain.cbdPercent}
            </p>
          )}
          {strain.floweringWeeks && (
            <p className="mb-1">
              <strong>{t('floweringWeeks')}:</strong> {strain.floweringWeeks} {t('weeks')}
            </p>
          )}
          {floweringCategoryLabel && (
            <p className="mb-1">
              <strong>{t('floweringCategory')}:</strong> {floweringCategoryLabel}
            </p>
          )}
          {yieldLabel && (
            <p className="mb-1">
              <strong>{t('yield')}:</strong> {yieldLabel}
            </p>
          )}
          {heightLabel && (
            <p className="mb-1">
              <strong>{t('plantHeight')}:</strong> {heightLabel}
            </p>
          )}
          {Array.isArray(strain.terpenes) && strain.terpenes.length > 0 && (
            <div className="mb-2">
              <strong>{t('terpenes')}:</strong>
              {renderTags(strain.terpenes)}
            </div>
          )}
          {Array.isArray(strain.aromaFlavor) && strain.aromaFlavor.length > 0 && (
            <div className="mb-2">
              <strong>{t('aromaFlavor')}:</strong>
              {renderTags(strain.aromaFlavor)}
            </div>
          )}
          {effectDirectionLabel && (
            <p className="mb-1">
              <strong>{t('effectDirection')}:</strong> {effectDirectionLabel}
            </p>
          )}
          {Array.isArray(strain.effectSensations) && strain.effectSensations.length > 0 && (
            <div className="mb-2">
              <strong>{t('effectSensations')}:</strong>
              {renderTags(strain.effectSensations)}
            </div>
          )}
        </div>
      )}
      {hasCorePlus && (
        <div className="mb-4">
          <h3 className="h5">{t('passportCorePlus')}</h3>
          {statusLabel && (
            <p className="mb-1">
              <strong>{t('status')}:</strong> {statusLabel}
            </p>
          )}
          {strain.uniqueTrait && (
            <p className="mb-1">
              <strong>{t('uniqueTrait')}:</strong> {strain.uniqueTrait}
            </p>
          )}
          {positioningLabel && (
            <p className="mb-1">
              <strong>{t('positioning')}:</strong> {positioningLabel}
            </p>
          )}
          {recommendedTimeLabel && (
            <p className="mb-1">
              <strong>{t('recommendedTime')}:</strong> {recommendedTimeLabel}
            </p>
          )}
          {targetAudienceLabel && (
            <p className="mb-1">
              <strong>{t('targetAudience')}:</strong> {targetAudienceLabel}
            </p>
          )}
        </div>
      )}
      {isAdmin && loggedIn && cuttings.length > 0 && (
        <div className="mt-4">
          <h3>{t('cuttings')}</h3>
          <ul className="list-unstyled">
            {cuttings.map((c) => (
              <li
                key={c._id}
                className="d-flex align-items-center mb-2"
              >
                {role === 'superuser' || role === 'admin' ? (
                  (() => {
                    const main = Array.isArray(c.images)
                      ? c.images.find((i) => i.isMain) || c.images[0]
                      : null;
                    const previewUrl =
                      main?.type === 'video'
                        ? main.thumbnailUrl || main.url
                        : main?.url;
                    if (main?.type === 'video') {
                      return (
                        <video
                          src={main.url}
                          poster={previewUrl || placeholder}
                          controls
                          className="me-2 img-thumbnail"
                          style={{ width: 150, height: 150, objectFit: 'cover' }}
                        />
                      );
                    }
                    return (
                      <img
                        src={previewUrl || c.photo || placeholder}
                        alt="preview"
                        loading="lazy"
                        className="me-2 img-thumbnail"
                        style={{ width: 150, height: 150, objectFit: 'cover' }}
                      />
                    );
                  })()
                ) : null}
                <span>
                  <Link to={`/cutting/${c._id}`}>{c.code}</Link>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isAdmin && loggedIn && (
        <div className="mt-4">
          <h3>{t('gardenerDiary')}</h3>
          <div className="alert alert-warning">
            {t('legacyDiaryReadOnly')}
            <div className="mt-2">
              {strain?._id ? (
                <Link to={`/reviews-admin?strainId=${encodeURIComponent(strain._id)}`}>
                  {t('goToReviews')}
                </Link>
              ) : (
                <Link to="/reviews-admin">{t('goToReviews')}</Link>
              )}
            </div>
          </div>
          {strain.diary && strain.diary.length > 0 ? (
            <ul className="list-unstyled">
              {strain.diary
                .slice()
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map((d) => (
                  <li key={d._id} className="mb-3">
                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex justify-content-between">
                          <small className="text-muted">
                            {new Date(d.timestamp).toLocaleString()}
                            {d.authorName ? ` - ${d.authorName}` : ''}
                          </small>
                        </div>
                        {d.note && <p className="mt-2 mb-0">{d.note}</p>}
                        {d.photos && d.photos.length > 0 && (
                          <div className="row mt-2">
                            {normalizeMediaList(d.photos || []).map((p, idx) => {
                              const key = p.publicId || p.url || idx;
                              return (
                                <div
                                  key={key}
                                  className="col-12 col-sm-6 col-md-4 col-lg-3"
                                >
                                  <div className="card mb-3">
                                    {p.type === 'video' ? (
                                      <video
                                        src={p.url}
                                        poster={p.thumbnailUrl || p.url}
                                        controls
                                        className="card-img-top img-fluid"
                                        style={{ objectFit: 'cover', height: '200px' }}
                                      />
                                    ) : (
                                      <img
                                        src={p.url}
                                        className="card-img-top img-fluid"
                                        style={{ objectFit: 'cover', height: '200px' }}
                                        alt=""
                                      />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          ) : (
            <p>{t('noData')}</p>
          )}
        </div>
      )}
    </div>
  );
}



